import { useAuth } from '@/hooks/useAuth'
import {ArrowRight, Camera, Check, Compass, Layers, Link2, Radio, Sparkles, Star, Users} from 'lucide-react'
import { Link } from 'react-router-dom'

const STEPS = [
  {
    icon: Camera,
    title: 'Snap your pulls',
    description: 'Upload real photos of Pokemon singles or sealed product straight from your phone.',
    accent: 'bg-[#FF1A1A]',
    tilt: '-rotate-2',
  },
  {
    icon: Link2,
    title: 'Attach your listings',
    description: 'Drop in your TCGplayer and eBay links so buyers can check out wherever you already sell.',
    accent: 'bg-[#3B4CCA]',
    tilt: 'rotate-1',
  },
  {
    icon: Users,
    title: 'Get discovered',
    description: 'Your singles and sealed product appear in the Pokemon feed and on your own storefront.',
    accent: 'bg-[#FFDE00] text-black',
    tilt: 'rotate-2',
  },
]

const FEATURES = [
  'A branded social profile & storefront page',
  'Unlimited photos of singles and sealed product',
  'One-click TCGplayer & eBay linking',
  'Discover stores by newest, oldest, or A to Z',
    'Live community feed of new listings',
  'Private messages with shops about singles, sealed product, and trades',
  'Toggle between singles and sealed product on every listing',
  'Mark cards or booster boxes as for sale with your asking price',
]

function MiniCard({
  src,
  alt,
  className,
  imageClassName,
}: {
  src: string
  alt: string
  className?: string
  imageClassName?: string
}) {
  return (
    <div className={`w-24 overflow-hidden rounded-[22px] border-4 border-black bg-white p-1.5 shadow-[8px_8px_0_rgba(0,0,0,0.35)] sm:w-32 ${className || ''}`}>
      <img
        src={src}
        alt={alt}
        className={`aspect-[3/4] w-full rounded-[16px] object-cover ${imageClassName || ''}`}
      />
    </div>
  )
}

function Home() {
  const { isAuthenticated } = useAuth()

  return (
    <div>
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 pt-12 pb-14 sm:px-6 sm:pt-16 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border-2 border-[#FFDE00] bg-[#FFDE00] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-black shadow-[0_3px_0_#c9a800]">
            <Layers className="h-3.5 w-3.5" />
            Free open beta
          </div>
          <h1 className="font-display max-w-xl text-[2.4rem] font-extrabold leading-[0.95] text-white sm:text-7xl">
            Show off your
            <span className="mx-2 inline-block -rotate-2 rounded-2xl bg-[#FF1A1A] px-2 py-1 text-white sm:px-3">hits</span>
            <span className="block pt-2">
              sell the
              <span className="ml-2 inline-block rotate-2 rounded-2xl bg-[#FFDE00] px-2 py-1 text-black sm:px-3">duplicates</span>
            </span>
          </h1>
          <p className="mt-5 max-w-lg text-base font-semibold text-white/70 sm:text-lg">
            My Card Shop is in open beta. Create a store, post Pokemon singles and sealed product, and send buyers straight to TCGplayer or eBay.
          </p>
          <div className="mt-8 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-start">
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#FFDE00] px-6 py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1"
              >
                Set up your store
                <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#FFDE00] px-6 py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1"
              >
                Join the open beta
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <Link
              to="/discover"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-white/20 bg-white/5 px-6 py-3 text-sm font-extrabold text-white transition-transform hover:-translate-y-1 hover:border-[#3B4CCA]"
            >
              <Compass className="h-4 w-4 text-[#8EA2FF]" />
              Discover stores
            </Link>
            <Link
              to="/feed"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-white/20 bg-white/5 px-6 py-3 text-sm font-extrabold text-white transition-transform hover:-translate-y-1 hover:border-[#3B4CCA]"
            >
              <Radio className="h-4 w-4 text-[#FF1A1A]" />
              Browse the live feed
            </Link>
          </div>
          <p className="mt-4 text-xs font-extrabold uppercase tracking-wide text-white/40">
            Free open beta · No payment needed
          </p>
        </div>

        <div className="relative mx-auto h-[260px] w-full max-w-md overflow-hidden sm:h-[340px] sm:overflow-visible">
          <div className="absolute left-6 top-10 h-24 w-24 rounded-full bg-[#3B4CCA]/40 blur-2xl" />
          <div className="absolute right-4 bottom-6 h-28 w-28 rounded-full bg-[#FFDE00]/20 blur-2xl" />
          <MiniCard
            src="https://static.lumi.new/77/778b8fb37ebe1148b645a46dd9ac43dc.webp"
            alt="Charizard ex card"
            className="animate-floaty absolute left-2 top-8"
          />
          <MiniCard
            src="https://static.lumi.new/1a/1ac8d9eb416748e1f040aa7b67d9159e.webp"
            alt="First Partner Illustration Collection"
            className="animate-floaty-delay absolute right-2 top-0"
            imageClassName="object-contain bg-white"
          />
          <MiniCard
            src="https://static.lumi.new/00/00835e00a2df762b51c91696e5f4d6cb.webp"
            alt="Pikachu card"
            className="animate-bob absolute bottom-4 left-1/2 -translate-x-1/2"
          />
          <Star className="animate-sparkle absolute right-8 top-36 h-7 w-7 fill-[#FFDE00] text-[#FFDE00]" />
          <Sparkles className="animate-sparkle absolute left-8 bottom-16 h-6 w-6 text-[#8EA2FF]" />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <div className="grid gap-4 sm:grid-cols-3">
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              className={`rounded-[28px] border-4 border-black bg-white/[0.06] p-5 shadow-[8px_8px_0_rgba(59,76,202,0.35)] transition-transform hover:-translate-y-1 ${step.tilt}`}
            >
              <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl text-lg font-extrabold text-white ${step.accent}`}>
                {i + 1}
              </div>
              <step.icon className="mb-2 h-5 w-5 text-[#FFDE00]" />
              <h3 className="font-display text-xl font-extrabold text-white">{step.title}</h3>
              <p className="mt-1.5 text-sm font-semibold text-white/60">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <div className="overflow-hidden rounded-[32px] border-4 border-[#3B4CCA] bg-gradient-to-br from-[#151a3d] via-[#111111] to-black p-6 shadow-[10px_10px_0_rgba(255,26,26,0.28)] sm:p-10">
          <div className="grid gap-8 sm:grid-cols-2 sm:items-center">
            <div>
              <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[#FF1A1A] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white">
                <Sparkles className="h-3.5 w-3.5" />
                Everything you need to sell
              </div>
              <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Built for collectors who sell.</h2>
              <p className="mt-2 text-sm font-semibold text-white/60">
                No more scattered posts across five apps. One profile holds your whole collection and points buyers straight to checkout.
              </p>
            </div>
            <ul className="grid gap-2.5">
              {FEATURES.map(feature => (
                <li key={feature} className="flex items-start gap-2.5 rounded-2xl bg-white/5 px-3 py-2 text-sm font-bold text-white/85">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FFDE00] text-black">
                    <Check className="h-3 w-3" />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-24 text-center sm:px-6">
        <div className="rounded-[32px] border-4 border-[#FFDE00] bg-[#FFDE00]/10 px-6 py-10">
          <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Ready to build your storefront?</h2>
          <p className="mx-auto mt-2 max-w-md text-sm font-semibold text-white/60">
            The open beta is free. Create your store, post listings, and share your shop link. Payments come later.
          </p>
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#FFDE00] px-6 py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1"
            >
              Go to your dashboard
              <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <Link
              to="/pricing"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#FFDE00] px-6 py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1"
            >
              Join the open beta
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </section>
    </div>
  )
}

export default Home
